import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface AppNotificationItem {
    /** Identifiant serveur (user_notification.id) */
    id: number | string;
    title: string;
    body: string;
    type: string;
    read: boolean;
    createdAt: number;
}

export interface CreateUserNotificationPayload {
    client_id: number;
    station_id: number;
    user_id: number;
    titre: string;
    message: string;
    type?: string;
    erp_product_id?: number | null;
    stock_product_id?: number | null;
    entrepot_id?: number | null;
    stock_id?: number | null;
}

@Injectable({ providedIn: 'root' })
export class AppNotificationsService {
    /**
     * Base notifications : port 3008 (environment.notifications_url), chemin api/notifications.
     */
    private getNotificationsResourceUrl(): string {
        const base =
            (environment as { notifications_url?: string }).notifications_url ??
            (environment as { stock_url?: string }).stock_url ??
            environment.apiUrl;
        const root = String(base).replace(/\/$/, '');
        if (root.endsWith('/api')) {
            return `${root}/notifications`;
        }
        return `${root}/api/notifications`;
    }

    private readonly _items = new BehaviorSubject<AppNotificationItem[]>([]);

    readonly items$: Observable<AppNotificationItem[]> = this._items.asObservable();

    readonly unreadCount$: Observable<number> = this._items.pipe(
        map((list) => list.filter((n) => !n.read).length)
    );

    constructor(private http: HttpClient) {}

    loadFromServer(): Observable<AppNotificationItem[]> {
        const ctx = this.getUserContext();
        if (!ctx) {
            return of([]);
        }
        let params = new HttpParams()
            .set('user_id', String(ctx.userId))
            .set('station_id', String(ctx.stationId))
            .set('client_id', String(ctx.clientId));
        return this.http.get<any>(this.getNotificationsResourceUrl(), { params }).pipe(
            map((res) => this.normalizeListResponse(res)),
            catchError(() => of([])),
            map((list) => {
                this._items.next(list);
                return list;
            })
        );
    }

    /**
     * POST /api/notifications — persistance en base (user_notification).
     */
    create(payload: CreateUserNotificationPayload): Observable<any> {
        const body: any = {
            client_id: payload.client_id,
            station_id: payload.station_id,
            user_id: payload.user_id,
            titre: payload.titre,
            message: payload.message,
            type: payload.type ?? 'stock_minimum',
            erp_product_id: payload.erp_product_id ?? null,
            stock_product_id: payload.stock_product_id ?? null,
            entrepot_id: payload.entrepot_id ?? null,
            stock_id: payload.stock_id ?? null
        };
        return this.http.post<any>(this.getNotificationsResourceUrl(), body).pipe(
            switchMap(() => this.loadFromServer()),
            catchError((err) => {
                console.warn('[AppNotificationsService] POST notifications', err);
                return of(null);
            })
        );
    }

    /** PUT /api/notifications/:id — marquer lu (ex. { lu: true } ou { lu: 1 }) */
    markRead(id: number | string): Observable<void> {
        const url = `${this.getNotificationsResourceUrl()}/${id}`;
        return this.http.put<any>(url, { lu: 1 }).pipe(
            switchMap(() => this.loadFromServer()),
            map(() => undefined),
            catchError(() => of(undefined))
        );
    }

    markAllRead(): void {
        const list = this._items.getValue().filter((n) => !n.read);
        if (list.length === 0) {
            return;
        }
        const reqs = list.map((n) =>
            this.http.put(`${this.getNotificationsResourceUrl()}/${n.id}`, { lu: 1 }).pipe(catchError(() => of(null)))
        );
        forkJoin(reqs).subscribe(() => {
            this.loadFromServer().subscribe();
        });
    }

    /** Sans DELETE backend : marquer tout comme lu (comportement « Effacer » côté métier) */
    clearAll(): void {
        this.markAllRead();
    }

    remove(id: number | string, event?: MouseEvent): void {
        event?.stopPropagation?.();
        this.markRead(id).subscribe();
    }

    private getUserContext(): { userId: number; stationId: number; clientId: number } | null {
        try {
            const raw = localStorage.getItem('currentUser');
            if (!raw) return null;
            const u = JSON.parse(raw);
            const userId = Number(u?.id);
            const stationId = Number(
                u?.stations?.[0]?.station_id ?? u?.stations?.[0]?.id ?? u?.station_id ?? 0
            );
            const clientId = Number(
                u?.client_id ?? u?.clientId ?? u?.id ?? 0
            );
            if (!userId || !stationId) return null;
            return { userId, stationId, clientId: clientId || userId };
        } catch {
            return null;
        }
    }

    private normalizeListResponse(res: any): AppNotificationItem[] {
        const raw =
            (Array.isArray(res?.data) && res.data) ||
            (Array.isArray(res?.data?.rows) && res.data.rows) ||
            (Array.isArray(res?.data?.notifications) && res.data.notifications) ||
            (Array.isArray(res?.notifications) && res.notifications) ||
            (Array.isArray(res) && res) ||
            [];
        if (!Array.isArray(raw)) {
            return [];
        }
        return raw.map((row: any) => this.mapRow(row));
    }

    private mapRow(row: any): AppNotificationItem {
        const id = row.id ?? row.notification_id;
        const lu = row.lu ?? row.read;
        const read =
            lu === true ||
            lu === 1 ||
            lu === '1' ||
            row.read === true;
        let createdAt = Date.now();
        if (row.created_at) {
            const t = new Date(row.created_at).getTime();
            if (!isNaN(t)) createdAt = t;
        }
        return {
            id,
            title: String(row.titre ?? row.title ?? ''),
            body: String(row.message ?? row.body ?? ''),
            type: String(row.type ?? 'stock_minimum'),
            read,
            createdAt
        };
    }
}
